// Éditeur d'une version de démarche : dix onglets (identité et fondement, conditions, formulaire,
// pièces, étapes et validateurs, délais, tarif, document final, conservation, textes usager),
// brouillon local automatique, enregistrement quand le contenu respecte le contrat, aperçu usager,
// soumission, renvoi et publication à quatre yeux. Une version publiée ou remplacée est en lecture.
import type { ContenuVersion } from '@cc/contrats';
import { Bandeau, Bouton, LienBouton, Onglets, PuceEtat, useBrouillon } from '@cc/ui';
import { ArrowLeft, Eye, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { ActionsVersion } from './ActionsVersion';
import { LIBELLES_ETAT_VERSION, normaliserContenu, problemesContenu, useDemarche, useTypesPieces, useVersion } from './contenu';
import { ConstructeurFormulaire } from './formulaire/ConstructeurFormulaire';
import { OngletConditions, OngletIdentite } from './onglets/OngletIdentite';
import { OngletDelaisTarif, OngletDocument, OngletTextes } from './onglets/OngletParametres';
import { OngletEtapes, OngletPieces } from './onglets/OngletPiecesEtapes';
import styles from './demarches.module.css';

const ONGLETS = ['identite', 'conditions', 'formulaire', 'pieces', 'etapes', 'delais', 'tarif', 'document', 'conservation', 'textes'] as const;
type IdOnglet = (typeof ONGLETS)[number];

function tonVersion(etat: string): 'neutre' | 'attente' | 'ok' {
  return etat === 'publiee' ? 'ok' : etat === 'en_verification' ? 'attente' : 'neutre';
}

function Editeur({ id, numero, brut, etat, soumiseLe }: { id: string; numero: number; brut: Record<string, unknown>; etat: string; soumiseLe: string | null }) {
  const { contexte } = useSession();
  const demarche = useDemarche(id);
  const typesPieces = useTypesPieces();
  const [params, setParams] = useSearchParams();
  const lectureSeule = etat !== 'brouillon' || !peut(contexte, 'demarche_config', 'modifier') || Boolean(demarche.data?.interne);
  const initial = useMemo(() => normaliserContenu(brut, demarche.data), [brut, demarche.data]);
  const [contenu, setContenu] = useState<ContenuVersion>(initial);
  const [modifie, setModifie] = useState(false);
  useEffect(() => {
    if (!modifie) setContenu(initial);
  }, [initial, modifie]);
  const brouillon = useBrouillon(lectureSeule ? null : `demarche-version-${id}-${numero}`, contenu, { actif: modifie });
  const versionPubliee = useVersion(id, demarche.data?.version_courante && demarche.data.version_courante.numero !== numero ? demarche.data.version_courante.numero : undefined);
  const contenuPublie = versionPubliee.data ? normaliserContenu(versionPubliee.data.contenu, demarche.data) : null;
  const problemes = problemesContenu(contenu);

  const enregistrement = useAction({
    executer: () => requete(`/demarches/${id}/versions/${numero}`, { methode: 'PUT', corps: { contenu } }),
    succes: 'Version enregistrée.',
    invalider: [['demarches']],
    apres: () => {
      setModifie(false);
      brouillon.effacer();
    },
  });
  const erreursServeur = enregistrement.erreur instanceof ErreurApi ? enregistrement.erreur.champs : [];

  const changer = (c: ContenuVersion) => {
    setContenu(c);
    setModifie(true);
  };
  const onglet = (params.get('onglet') as IdOnglet | null) ?? 'identite';
  const commun = { contenu, onChanger: changer, lectureSeule };
  const d = demarche.data;

  return (
    <>
      <EnTetePage
        etiquette={`Démarches / ${d?.libelle ?? ''} / Version ${numero}`}
        titre={contenu.libelle || d?.libelle || 'Version'}
        description={
          <div className={styles.statut}>
            <PuceEtat ton={tonVersion(etat)} libelle={LIBELLES_ETAT_VERSION[etat] ?? etat} />
            {d?.version_courante ? <span>Version {d.version_courante.numero} publiée le {date(d.version_courante.publiee_le)}</span> : <span>Aucune version publiée</span>}
            {soumiseLe ? <span>Soumise le {date(soumiseLe)}</span> : null}
            {d?.interne ? <span>Démarche interne de test, non modifiable</span> : null}
            {lectureSeule && etat === 'brouillon' && !d?.interne ? <span>Lecture seule : droit de modification de la configuration requis</span> : null}
          </div>
        }
        actions={
          <div className={styles.actionsEditeur}>
            <LienBouton href={`/demarches/${id}`} icone={ArrowLeft}>
              Fiche de la démarche
            </LienBouton>
            <LienBouton href={`/demarches/${id}/versions/${numero}/apercu`} icone={Eye}>
              Aperçu usager
            </LienBouton>
            {!lectureSeule ? (
              <Bouton variante={modifie ? 'principal' : 'secondaire'} icone={Save} chargement={enregistrement.enCours} disabled={!modifie || problemes.length > 0} onClick={() => enregistrement.lancer(undefined)}>
                Enregistrer
              </Bouton>
            ) : null}
            {d ? <ActionsVersion demarche={d} version={{ numero, etat: etat as 'brouillon', soumise_le: soumiseLe }} contenu={contenu} contenuPublie={contenuPublie} modifie={modifie} /> : null}
          </div>
        }
      />
      {brouillon.brouillonDisponible && !modifie ? (
        <Bandeau type="info" onFermer={brouillon.ignorer}>
          Un brouillon non enregistré de cette version existe sur cet appareil{brouillon.brouillonDate ? ` (${date(brouillon.brouillonDate)})` : ''}.{' '}
          <Bouton petit variante="secondaire" onClick={() => { changer(brouillon.brouillonDisponible as ContenuVersion); brouillon.ignorer(); }}>
            Reprendre le brouillon
          </Bouton>{' '}
          <Bouton petit variante="discret" onClick={brouillon.effacer}>
            L'oublier
          </Bouton>
        </Bandeau>
      ) : null}
      {modifie ? <p role="status" className={styles.aideDiscrete}>Modifications non enregistrées{brouillon.enregistreLe ? `, brouillon conservé sur cet appareil à ${brouillon.enregistreLe.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}.</p> : null}
      {modifie && problemes.length > 0 ? (
        <Bandeau type="attention" titre="À compléter avant d'enregistrer">
          <ul className={styles.problemes}>
            {problemes.slice(0, 8).map((p, i) => (
              <li key={i}>
                <code>{p.chemin || 'contenu'}</code> : {p.message}
              </li>
            ))}
            {problemes.length > 8 ? <li>et {problemes.length - 8} autres</li> : null}
          </ul>
        </Bandeau>
      ) : null}
      {erreursServeur.length > 0 ? (
        <Bandeau type="erreur" titre="Refusé par le serveur">
          <ul className={styles.problemes}>
            {erreursServeur.map((e, i) => (
              <li key={i}>
                <code>{e.champ}</code> : {e.message}
              </li>
            ))}
          </ul>
        </Bandeau>
      ) : enregistrement.messageErreur ? (
        <Bandeau type="erreur">{enregistrement.messageErreur}</Bandeau>
      ) : null}
      <Onglets
        libelle="Paramètres de la démarche"
        actif={ONGLETS.includes(onglet) ? onglet : 'identite'}
        onChanger={(o) => setParams((p) => { p.set('onglet', o); return p; }, { replace: true })}
        onglets={[
          { id: 'identite', libelle: 'Identité et fondement', contenu: <OngletIdentite {...commun} /> },
          { id: 'conditions', libelle: 'Conditions', contenu: <OngletConditions {...commun} /> },
          { id: 'formulaire', libelle: 'Formulaire', contenu: <ConstructeurFormulaire formulaire={contenu.formulaire} onChanger={(formulaire) => changer({ ...contenu, formulaire })} typesPieces={typesPieces.data ?? []} rubriqueTarif={contenu.tarif.rubrique} lectureSeule={lectureSeule} /> },
          { id: 'pieces', libelle: `Pièces (${contenu.pieces.length})`, contenu: <OngletPieces {...commun} /> },
          { id: 'etapes', libelle: `Étapes (${contenu.etapes.length})`, contenu: <OngletEtapes {...commun} /> },
          { id: 'delais', libelle: 'Délais', contenu: <OngletDelaisTarif {...commun} partie="delais" /> },
          { id: 'tarif', libelle: 'Tarif', contenu: <OngletDelaisTarif {...commun} partie="tarif" /> },
          { id: 'document', libelle: 'Document final', contenu: <OngletDocument {...commun} partie="document" /> },
          { id: 'conservation', libelle: 'Conservation', contenu: <OngletDocument {...commun} partie="conservation" /> },
          { id: 'textes', libelle: 'Textes usager', contenu: <OngletTextes {...commun} /> },
        ]}
      />
    </>
  );
}

export function PageEditeurVersion() {
  const { id = '', v = '' } = useParams();
  const numero = Number(v);
  const version = useVersion(id, numero);
  return (
    <EtatRequete chargement={version.isPending} erreur={version.error} onReessayer={() => void version.refetch()} lot="L1-A" fonction="L'éditeur de démarches" lignes={8}>
      {version.data ? <Editeur key={`${id}-${numero}-${version.data.etat}`} id={id} numero={numero} brut={version.data.contenu} etat={version.data.etat} soumiseLe={version.data.soumise_le} /> : null}
    </EtatRequete>
  );
}
